import React, { useEffect, useState, useRef } from 'react';
import { X, Minus, MessageCircle, Wifi, WifiOff, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/stores/chatStore';
import { ChatApiService } from '@/services/chatApi';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import type { ChatConfig, Message, MessageContent, UIResource } from '@/types/chat';
import { nanoid } from 'nanoid';
import { IChatConnection, chatConnectionFactory } from '@/services/chat';
import { useIdentity } from '@/hooks/useIdentity';

interface ChatWidgetProps {
  config?: ChatConfig;
  // Alternative props for widget mode
  workflowId?: string;
  apiEndpoint?: string;
  title?: string;
  subtitle?: string;
  initialMessages?: string[];
  inputPlaceholder?: string;
  placeholder?: string;
  sessionId?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onMessage?: (message: any) => void;
  onResponse?: (message: any) => void;
  onError?: (error: Error) => void;
  onSessionCreated?: (sessionId: string) => void;
  mode?: 'widget' | 'fullscreen';
  className?: string;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ 
  config: configProp,
  workflowId,
  apiEndpoint,
  title,
  subtitle,
  initialMessages,
  inputPlaceholder,
  placeholder,
  sessionId: sessionIdProp,
  isOpen: isOpenProp,
  onOpenChange,
  onMessage: onMessageProp,
  onResponse: onResponseProp,
  onError: onErrorProp,
  onSessionCreated,
  mode = 'widget',
  className,
}) => {
  // Build config from props or use provided config
  const config: ChatConfig = configProp || {
    workflowId: workflowId || '',
    apiEndpoint: apiEndpoint || '',
    title: title || 'AI Assistant',
    subtitle: subtitle,
    placeholder: placeholder || inputPlaceholder || 'Type your message...',
    initialMessages: initialMessages,
  };

  const {
    isOpen: storeIsOpen,
    isMinimized,
    messages,
    isTyping,
    isConnected,
    error,
    sessionId,
    setConfig,
    setOpen: storeSetOpen,
    setMinimized,
    addMessage,
    updateMessage,
    setTyping,
    setConnected,
    setError,
    setSessionId,
    setMessages,
  } = useChatStore();

  // Use controlled or uncontrolled mode
  const isOpen = isOpenProp !== undefined ? isOpenProp : storeIsOpen;
  const setOpen = (open: boolean) => {
    if (onOpenChange) {
      onOpenChange(open);
    } else {
      storeSetOpen(open);
    }
  };

  const [apiService, setApiService] = useState<ChatApiService | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const chatConnectionRef = useRef<IChatConnection | null>(null);
  const pendingMessagesRef = useRef<Map<string, string>>(new Map()); // Map executionId -> tempMessageId
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get identity for authentication
  const { getAccessToken } = useIdentity();
  
  // Chat event handlers (defined before useEffect to avoid dependency issues)
  const handleChatResponse = (data: { sessionId: string; executionId: string; message: string | object }) => {
    console.log('[ChatWidget] 📨 Received chat response', data);
    console.log('[ChatWidget] 📦 Message type:', typeof data.message);
    console.log('[ChatWidget] 📦 Message value:', data.message);
    
    // Clear the typing timeout since we received a response
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    
    // Parse message content - could be string or UIResource
    let content: MessageContent;
    
    if (typeof data.message === 'object' && data.message !== null) {
      console.log('[ChatWidget] 📦 Message is object, checking for UIResource...');
      console.log('[ChatWidget] 📦 Has type field:', 'type' in data.message);
      console.log('[ChatWidget] 📦 Type value:', (data.message as any).type);
      
      // Check if it's a UIResource
      if ('type' in data.message && (data.message as any).type === 'resource') {
        console.log('[ChatWidget] ✅ Detected UIResource!');
        content = data.message as UIResource;
      } else {
        // Unknown object, stringify it
        console.log('[ChatWidget] ⚠️ Object is not a UIResource, stringifying');
        content = JSON.stringify(data.message, null, 2);
      }
    } else if (typeof data.message === 'string') {
      console.log('[ChatWidget] 📦 Message is string, checking if JSON...');
      // Try to parse as JSON (in case it was serialized)
      try {
        const parsed = JSON.parse(data.message);
        if (parsed.type === 'resource') {
          console.log('[ChatWidget] ✅ Parsed string as UIResource!');
          content = parsed as UIResource;
        } else {
          console.log('[ChatWidget] 📝 Parsed JSON but not a UIResource');
          content = data.message;
        }
      } catch {
        // Not JSON, just a string
        console.log('[ChatWidget] 📝 String is not JSON, using as-is');
        content = data.message;
      }
    } else {
      console.log('[ChatWidget] ⚠️ Unknown message type, converting to string');
      content = String(data.message);
    }
    
    console.log('[ChatWidget] 📤 Final content type:', typeof content);
    console.log('[ChatWidget] 📤 Final content:', content);
    
    // Add the AI response message
    const aiMessage: Message = {
      id: nanoid(),
      sessionId: data.sessionId,
      role: 'assistant',
      content,
      timestamp: new Date(),
      status: 'sent',
    };
    addMessage(aiMessage);

    setTyping(false);
    setIsLoading(false);

    // Trigger callback
    if (onResponseProp) {
      const callbackContent = typeof content === 'string' ? content : JSON.stringify(content);
      onResponseProp({ content: callbackContent, executionId: data.executionId });
    }
  };

  const handleChatError = (data: { sessionId: string; executionId: string; error: string }) => {
    console.error('Chat error:', data);
    
    // Clear the typing timeout since we received an error
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    
    // Add an error message
    const errorMessage: Message = {
      id: nanoid(),
      sessionId: data.sessionId,
      role: 'assistant',
      content: `Error: ${data.error}`,
      timestamp: new Date(),
      status: 'error',
    };
    addMessage(errorMessage);

    setTyping(false);
    setIsLoading(false);
    setError(data.error);

    if (onErrorProp) {
      onErrorProp(new Error(data.error));
    }
  };

  // Initialize API service and session
  useEffect(() => {
    setConfig(config);

    // Ensure endpoint includes /api
    let endpoint = config.apiEndpoint || 'http://localhost:5000/api';
    if (!endpoint.endsWith('/api')) {
      endpoint = endpoint + '/api';
    }
    
    const service = new ChatApiService(endpoint, config.apiKey);
    setApiService(service);

    // Initialize chat connection for both regular and hosted chat
    // Skip if connection already exists
    if (chatConnectionRef.current) {
      console.log('[ChatWidget] Chat connection already exists, skipping initialization');
      return;
    }

    const initChatConnection = async () => {
      try {
        console.log('[ChatWidget] Initializing chat connection...');
        console.log('[ChatWidget] Endpoint:', endpoint);
        
        const backendUrl = endpoint.replace('/api', ''); // Remove /api for connection
        console.log('[ChatWidget] Backend URL:', backendUrl);
        
        const token = await getAccessToken();
        console.log('[ChatWidget] Got access token:', token ? 'yes' : 'no');
        
        // Create .NET backend connection
        console.log('[ChatWidget] Creating chat connection for .NET backend...');
        const chatConnection = chatConnectionFactory.create(
          'dotnet',
          backendUrl,
          token
        );
        
        chatConnectionRef.current = chatConnection;
        console.log('[ChatWidget] Chat connection created, connecting...');
        
        // Connect with event handlers
        await chatConnection.connect({
          sessionId: sessionId || '', // Will subscribe when session is created
          onConnected: () => {
            console.log('[ChatWidget] Chat connection established');
            setConnected(true);
          },
          onDisconnected: (reason) => {
            console.log('[ChatWidget] Chat connection lost:', reason);
            setConnected(false);
          },
          onError: (error) => {
            console.error('[ChatWidget] Chat connection error:', error);
            setConnected(false);
          },
          onMessage: handleChatResponse,
          onChatError: handleChatError,
        });
        
        console.log('[ChatWidget] Chat connection initialized successfully');
      } catch (error) {
        console.error('[ChatWidget] Failed to initialize chat connection:', error);
        console.error('[ChatWidget] Error stack:', error instanceof Error ? error.stack : 'no stack');
        setConnected(false);
        // Don't throw - just log and set disconnected state
      }
    };
    
    initChatConnection();

    // Cleanup on unmount
    return () => {
      if (chatConnectionRef.current) {
        try {
          chatConnectionRef.current.disconnect();
          chatConnectionRef.current = null;
        } catch (error) {
          console.error('[ChatWidget] Error disconnecting chat connection:', error);
        }
      }
    };
  }, [config.apiKey, config.apiEndpoint]);

  // Initialize session on mount
  // (separate effect to avoid re-running on every config change)
  useEffect(() => {
    if (!apiService) return;

    const initSession = async () => {
      try {
        console.log('[ChatWidget] 🚀 Initializing session...', {
          workflowId: config.workflowId,
          sessionIdProp,
          configSessionId: config.sessionId,
          storeSessionId: sessionId
        });

        if (!config.workflowId) {
          console.error('[ChatWidget] ❌ No workflowId provided');
          setError('Configuration error: No workflow ID');
          return;
        }

        // Use sessionId from props, config, or store (in that order)
        const existingSessionId = sessionIdProp || config.sessionId || sessionId;
        console.log('[ChatWidget] 📝 Existing session ID:', existingSessionId);

        if (!existingSessionId) {
          console.log('[ChatWidget] 📝 Creating new session...', {
            workflowId: config.workflowId,
            userId: config.metadata?.userId,
            metadata: config.metadata
          });
          const response = await apiService.createSession({
            workflowId: config.workflowId,
            userId: config.metadata?.userId,
            metadata: config.metadata,
          });
          console.log('[ChatWidget] ✅ Session created:', response);
          // Backend returns 'id' property, not 'sessionId'
          const newSessionId = (response as any).id || response.sessionId;
          console.log('[ChatWidget] 📝 Setting session ID:', newSessionId);
          setSessionId(newSessionId);
          
          // Call callback if provided
          if (onSessionCreated) {
            onSessionCreated(newSessionId);
          }
          
          // Add initial messages if provided
          if (config.initialMessages && config.initialMessages.length > 0) {
            const initialMsgs: Message[] = config.initialMessages.map((content, index) => ({
              id: `initial-${index}`,
              sessionId: newSessionId,
              role: 'assistant' as const,
              content,
              timestamp: new Date(Date.now() + index), // Ensure ordering
              status: 'sent' as const,
            }));
            setMessages(initialMsgs);
          }
        } else {
          // Use existing session ID (from props or config)
          setSessionId(existingSessionId);
          
          // Always load history for existing sessions
          try {
            const history = await apiService.getHistory(existingSessionId);
            if (history.messages && history.messages.length > 0) {
              // Deserialize UIResource objects from stored messages
              const deserializedMessages = history.messages.map((msg) => {
                // Check if this was a resource message
                if (msg.metadata?.isResource && typeof msg.content === 'string') {
                  try {
                    const parsed = JSON.parse(msg.content);
                    if (parsed.type === 'resource') {
                      return { ...msg, content: parsed as UIResource };
                    }
                  } catch {
                    // Failed to parse, keep as string
                  }
                }
                return msg;
              });
              setMessages(deserializedMessages);
            } else {
              // No history found, show initial messages if provided
              if (config.initialMessages && config.initialMessages.length > 0) {
                const initialMsgs: Message[] = config.initialMessages.map((content, index) => ({
                  id: `initial-${index}`,
                  sessionId: existingSessionId,
                  role: 'assistant' as const,
                  content,
                  timestamp: new Date(Date.now() + index), // Ensure ordering
                  status: 'sent' as const,
                }));
                setMessages(initialMsgs);
              }
            }
          } catch (err) {
            // If history fails, show initial messages if provided
            if (config.initialMessages && config.initialMessages.length > 0) {
              const initialMsgs: Message[] = config.initialMessages.map((content, index) => ({
                id: `initial-${index}`,
                sessionId: existingSessionId,
                role: 'assistant' as const,
                content,
                timestamp: new Date(Date.now() + index), // Ensure ordering
                status: 'sent' as const,
              }));
              setMessages(initialMsgs);
            }
          }
        }
      } catch (err) {
        console.error('[ChatWidget] ❌ Failed to initialize session:', err);
        console.error('[ChatWidget] ❌ Error details:', {
          message: err instanceof Error ? err.message : 'Unknown error',
          stack: err instanceof Error ? err.stack : undefined,
          error: err
        });
        setError('Failed to connect. Please try again.');
      }
    };

    initSession();

    // Set initial open state
    if (config.initialOpen) {
      setOpen(true);
    }
  }, [apiService, config.workflowId, config.sessionId]);

  // Subscribe to session when sessionId changes OR when connection becomes ready
  useEffect(() => {
    console.log('[ChatWidget] 🔔 Session subscription effect triggered', {
      hasConnection: !!chatConnectionRef.current,
      sessionId,
      isConnected: chatConnectionRef.current?.isConnected,
      storeIsConnected: isConnected
    });

    if (!chatConnectionRef.current || !sessionId) {
      console.log('[ChatWidget] ⚠️ Cannot subscribe - missing connection or sessionId', {
        hasConnection: !!chatConnectionRef.current,
        sessionId
      });
      return;
    }

    const chatConnection = chatConnectionRef.current;

    // Subscribe to session
    // Use a small delay to ensure the connection is fully established
    setTimeout(() => {
      console.log('[ChatWidget] 📡 Attempting to subscribe to session:', {
        sessionId,
        isConnected: chatConnection.isConnected,
        connectionExists: !!chatConnection
      });
      
      if (chatConnection.isConnected) {
        console.log('[ChatWidget] 📡 Subscribing to session:', sessionId);
        chatConnection.subscribeToSession(sessionId);
      } else {
        console.warn('[ChatWidget] ⚠️ Cannot subscribe - connection not ready', {
          isConnected: chatConnection.isConnected,
          sessionId
        });
      }
    }, 100); // Small delay to ensure connection state is updated

    // Cleanup - unsubscribe when component unmounts or sessionId changes
    return () => {
      if (chatConnection.isConnected) {
        console.log('[ChatWidget] 🔕 Unsubscribing from session:', sessionId);
        chatConnection.unsubscribeFromSession(sessionId);
      }
    };
  }, [sessionId, isConnected]);

  const handleSendMessage = async (content: string) => {
    console.log('[ChatWidget] 📤 handleSendMessage called', { 
      content, 
      apiService: !!apiService, 
      sessionId, 
      isLoading 
    });

    if (!apiService || !sessionId || isLoading) {
      console.log('[ChatWidget] ❌ Cannot send message - missing requirements', {
        hasApiService: !!apiService,
        hasSessionId: !!sessionId,
        isLoading
      });
      return;
    }

    // Create optimistic user message
    const userMessage: Message = {
      id: nanoid(),
      sessionId,
      role: 'user',
      content,
      timestamp: new Date(),
      status: 'sending',
      metadata: config.metadata,
    };

    console.log('[ChatWidget] 📝 Created user message', userMessage);
    addMessage(userMessage);
    setIsLoading(true);
    setTyping(true);

    try {
      if (!config.workflowId) {
        throw new Error('No workflow ID configured');
      }

      console.log('[ChatWidget] 🚀 Sending message to API...', {
        workflowId: config.workflowId,
        sessionId,
        content: content.substring(0, 50) + '...'
      });

      // Send message to API
      const response = await apiService.sendMessageWithWorkflow(
        config.workflowId,
        sessionId,
        content,
        config.metadata?.userId,
        config.metadata
      );

      console.log('[ChatWidget] ✅ Message sent successfully', response);

      // Update user message status
      updateMessage(userMessage.id, { status: 'sent' });

      // If response includes aiResponse, add it immediately (HTTP fallback)
      // Don't process HTTP response - wait for SignalR to broadcast messages
      // SignalR will send both text responses and UIResources
      console.log('[ChatWidget] ⏳ Waiting for SignalR to broadcast responses...');
      
      // Keep typing indicator on - it will be turned off when SignalR response arrives
      // Set a timeout to turn off typing indicator if no response arrives (safety measure)
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        setTyping(false);
        setIsLoading(false);
        setError('No response received from the workflow. Please try again.');
      }, 60000); // 60 second timeout

      // Trigger callbacks
      config.onMessageSent?.(userMessage);
      onMessageProp?.(userMessage);
    } catch (err) {
      console.error('Failed to send message:', err);
      
      // Clear the typing timeout on error
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      
      updateMessage(userMessage.id, { status: 'error' });
      const error = err instanceof Error ? err : new Error('Failed to send message');
      setError(error.message);
      setTyping(false); // Turn off typing indicator on error
      onErrorProp?.(error);
    } finally {
      setIsLoading(false);
      // Don't turn off typing here - let the Socket.IO response handler do it
    }
  };

  const handleToggle = () => {
    if (isMinimized) {
      setMinimized(false);
    } else {
      setOpen(!isOpen);
    }
  };

  const handleMinimize = () => {
    setMinimized(true);
  };

  const handleClose = () => {
    setOpen(false);
    setMinimized(false);
  };

  const handleNewConversation = async () => {
    if (!apiService || !config.workflowId) return;
    
    try {
      // Create a new session
      const response = await apiService.createSession({
        workflowId: config.workflowId,
        userId: config.metadata?.userId,
        metadata: config.metadata,
      });
      
      // Update session ID
      setSessionId(response.sessionId);
      
      // Clear messages and add initial messages if configured
      if (config.initialMessages && config.initialMessages.length > 0) {
        const initialMsgs: Message[] = config.initialMessages.map((content, index) => ({
          id: `initial-${index}`,
          sessionId: response.sessionId,
          role: 'assistant' as const,
          content,
          timestamp: new Date(Date.now() + index),
          status: 'sent' as const,
        }));
        setMessages(initialMsgs);
      } else {
        setMessages([]);
      }
      
      // Clear any errors
      setError(null);
    } catch (err) {
      console.error('Failed to start new conversation:', err);
      setError('Failed to start new conversation. Please try again.');
    }
  };

  // Position classes
  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
  };

  const position = config.position || 'bottom-right';

  // Fullscreen mode - no positioning, just fill container
  if (mode === 'fullscreen') {
    return (
      <div
        className={cn('flex flex-col h-full', className)}
        style={{
          '--chat-primary': config.primaryColor || 'hsl(var(--primary))',
          '--chat-font-family': config.fontFamily || 'inherit',
          '--chat-border-radius': config.borderRadius || '12px',
        } as React.CSSProperties}
      >
        {/* Error Banner */}
        {error && (
          <div className="bg-destructive/10 text-destructive px-4 py-2 text-sm border-b">
            {error}
          </div>
        )}

        {/* Messages */}
        <MessageList
          messages={messages}
          isTyping={isTyping}
          welcomeMessage={config.welcomeMessage}
        />

        {/* Input */}
        <MessageInput
          onSend={handleSendMessage}
          disabled={!isConnected}
          placeholder={config.placeholder}
          maxLength={config.maxMessageLength}
          isLoading={isLoading}
        />
      </div>
    );
  }

  // Widget mode - floating widget
  return (
    <div
      className={cn('fixed z-50', positionClasses[position], className)}
      style={{
        '--chat-primary': config.primaryColor || 'hsl(var(--primary))',
        '--chat-font-family': config.fontFamily || 'inherit',
        '--chat-border-radius': config.borderRadius || '12px',
      } as React.CSSProperties}
    >
      {/* Chat Window */}
      {isOpen && (
        <Card
          className={cn(
            'w-[400px] h-[600px] flex flex-col shadow-2xl mb-4 overflow-hidden',
            'animate-in fade-in slide-in-from-bottom-4 duration-300',
            isMinimized && 'h-[60px]'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-primary text-primary-foreground">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                <MessageCircle className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">{config.title || 'AI Assistant'}</h3>
                <div className="flex items-center gap-1 text-xs opacity-90">
                  {config.subtitle ? (
                    <span>{config.subtitle}</span>
                  ) : isConnected ? (
                    <>
                      <Wifi className="w-3 h-3" />
                      <span>Online</span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-3 h-3" />
                      <span>Offline</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                onClick={handleNewConversation}
                title="New Conversation"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                onClick={handleMinimize}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                onClick={handleClose}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          {!isMinimized && (
            <>
              {/* Error Banner */}
              {error && (
                <div className="bg-destructive/10 text-destructive px-4 py-2 text-sm border-b">
                  {error}
                </div>
              )}

              {/* Messages */}
              <MessageList
                messages={messages}
                isTyping={isTyping}
                welcomeMessage={config.welcomeMessage}
              />

              {/* Input */}
              <MessageInput
                onSend={handleSendMessage}
                disabled={!isConnected}
                placeholder={config.placeholder}
                maxLength={config.maxMessageLength}
                isLoading={isLoading}
              />
            </>
          )}
        </Card>
      )}

      {/* Toggle Button */}
      {!isOpen && (
        <Button
          onClick={handleToggle}
          size="icon"
          className="h-14 w-14 rounded-full shadow-lg animate-in fade-in zoom-in duration-300"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}
    </div>
  );
};
